import { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import { GetInventoryLevels } from "api/rpc/2024-04/facilityAdmin/product/inventory";
import { GetRegisterInventory } from "api/rpc/2024-04/facilityAdmin/product/registerInventory";
import { IProduct, IVariant, TProductInventoryLevel } from "redux/reducers/models/product";

export function variantHasRegisterQuantity(variant: IVariant, registerId: number): boolean {
  const registerLevels = variant.inventory_item?.inventory_level?.register_inventory_levels;
  if (!Array.isArray(registerLevels)) {
    return false;
  }

  return registerLevels.some(
    level => Number(level.register_id) === Number(registerId) && Number(level.quantity_available) > 0,
  );
}

export function getStoredRegisterId(): number | undefined {
  try {
    const register = JSON.parse(localStorage.getItem("register") || "null") as { id?: number } | null;
    return register?.id;
  } catch {
    return undefined;
  }
}

export async function fetchTrackedInventoryQuantities(
  variantIds: number[],
  useGlobalLoader: boolean,
  cancelToken?: CancelToken,
): Promise<{ status: StatusCode; getQuantity: (variant: IVariant) => number | undefined }> {
  const registerId = getStoredRegisterId();

  if (registerId) {
    const res = await GetRegisterInventory(
      { register_id: registerId, variant_ids: variantIds },
      useGlobalLoader,
      cancelToken,
    );
    const levels = res.status === StatusCode.OK ? res.data ?? [] : [];
    return {
      status: res.status,
      getQuantity: variant => {
        const level = levels.find(registerLevel => registerLevel.variant_id === variant.id);
        if (!level) {
          return 0;
        }

        return level.quantity_available ?? 0;
      },
    };
  }

  const res = await GetInventoryLevels({ variant_ids: variantIds }, useGlobalLoader, cancelToken);
  const levels: TProductInventoryLevel[] = res.status === StatusCode.OK ? (res.data as TProductInventoryLevel[] | undefined) ?? [] : [];
  return {
    status: res.status,
    getQuantity: variant => {
      const level = levels.find(
        inventoryLevel => inventoryLevel?.id === variant?.inventory_item?.inventory_level?.id,
      );
      return level?.quantity_available;
    },
  };
}

export function getQuantityAvailableForSelectedRegister(variant: IVariant): number | undefined {
  const registerId = getStoredRegisterId();
  const inventoryLevel = variant?.inventory_item?.inventory_level;
  const registerLevels = inventoryLevel?.register_inventory_levels;
  const facilityQuantity = Number(inventoryLevel?.quantity_available ?? 0);

  if (!Array.isArray(registerLevels)) {
    return inventoryLevel?.quantity_available;
  }

  const assignedQuantity = registerLevels.reduce(
    (sum, level) => sum + Number(level.quantity_available ?? 0),
    0,
  );
  const unassignedQuantity = Math.max(facilityQuantity - assignedQuantity, 0);

  if (!registerId) {
    return unassignedQuantity;
  }

  const registerLevel = registerLevels.find(level => Number(level.register_id) === Number(registerId));
  if (registerLevel) {
    return Number(registerLevel.quantity_available ?? 0);
  }

  return unassignedQuantity;
}

export function applyInventoryQuantitiesToProducts(
  products: IProduct[],
  productIds: number[],
  getQuantity: (variant: IVariant) => number | undefined,
): IProduct[] {
  const registerId = getStoredRegisterId();

  return products.map(product => {
    if (!productIds.includes(product.id)) {
      return product;
    }

    return {
      ...product,
      variants: product.variants?.map(variant => {
        const quantity = getQuantity(variant);
        const inventoryLevel = variant.inventory_item?.inventory_level;
        const registerLevels = inventoryLevel?.register_inventory_levels;

        if (registerId && Array.isArray(registerLevels)) {
          if (quantity === undefined) {
            return variant;
          }

          return {
            ...variant,
            inventory_item: {
              ...variant.inventory_item,
              inventory_level: {
                ...inventoryLevel,
                register_inventory_levels: registerLevels.map(level =>
                  Number(level.register_id) === Number(registerId)
                    ? { ...level, quantity_available: quantity }
                    : level,
                ),
              },
            },
          };
        }

        return {
          ...variant,
          inventory_item: {
            ...variant.inventory_item,
            inventory_level:
              quantity !== undefined
                ? {
                    ...inventoryLevel,
                    quantity_available: quantity,
                  }
                : { ...inventoryLevel },
          },
        };
      }),
    };
  });
}
